import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { User } from "@supabase/supabase-js";
import type { Database } from "@/shared/db";
import type {
  SessionStatus,
  SessionError,
  SessionErrorType,
} from "../shared/types/session.types";
import { isValidTransition } from "../shared/types/session.types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

const STORAGE_VERSION = 2;
const STORAGE_KEY = "lawgentia-auth-storage";

interface AuthState {
  // Session state
  sessionStatus: SessionStatus;
  user: User | null;
  profile: Profile | null;
  isAdmin: boolean;
  adminRole: string | null;
  error: SessionError | null;
  lastSuccessfulSync: number | null;
  rehydrated: boolean;
  storageVersion: number;

  // Actions
  setSessionStatus: (status: SessionStatus) => void;
  setSession: (user: User | null) => void;
  setProfile: (profile: Profile | null) => void;
  setAdminStatus: (isAdmin: boolean, role: string | null) => void;
  setError: (
    type: SessionErrorType,
    message: string,
    retryable: boolean,
    originalError?: Error,
  ) => void;
  clearError: () => void;
  setAuthenticated: (
    user: User,
    profile: Profile,
    isAdmin: boolean,
    adminRole: string | null,
  ) => void;
  setUnauthenticated: () => void;
  updateLastSync: () => void;
  incrementRetryCount: () => void;
  reset: () => void;
  setRehydrated: (rehydrated: boolean) => void;
}

const initialState = {
  sessionStatus: "idle" as SessionStatus,
  user: null,
  profile: null,
  isAdmin: false,
  adminRole: null,
  error: null,
  lastSuccessfulSync: null,
  rehydrated: false,
  storageVersion: STORAGE_VERSION,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSessionStatus: (status: SessionStatus) => {
        const currentStatus = get().sessionStatus;

        // Validate state transition
        if (!isValidTransition(currentStatus, status)) {
          console.warn(
            `[AuthStore] Invalid state transition: ${currentStatus} -> ${status}`,
          );
          return;
        }

        if (process.env.NODE_ENV === "development") {
          console.log(
            `[AuthStore] State transition: ${currentStatus} -> ${status}`,
          );
        }

        set({ sessionStatus: status });
      },

      setSession: (user) => set({ user }),

      setProfile: (profile) => set({ profile }),

      setAdminStatus: (isAdmin, adminRole) => set({ isAdmin, adminRole }),

      setError: (type, message, retryable, originalError) => {
        const currentError = get().error;
        const retryCount = currentError?.retryCount ?? 0;

        set({
          sessionStatus: "error",
          error: {
            type,
            message,
            retryable,
            retryCount,
            originalError,
            timestamp: Date.now(),
          },
        });
      },

      clearError: () => set({ error: null }),

      setAuthenticated: (user, profile, isAdmin, adminRole) => {
        // Atomic update - set all auth data at once
        set({
          sessionStatus: "authenticated",
          user,
          profile,
          isAdmin,
          adminRole,
          error: null,
          lastSuccessfulSync: Date.now(),
        });
      },

      setUnauthenticated: () => {
        set({
          sessionStatus: "unauthenticated",
          user: null,
          profile: null,
          isAdmin: false,
          adminRole: null,
          error: null,
        });
      },

      updateLastSync: () => {
        set({ lastSuccessfulSync: Date.now() });
      },

      incrementRetryCount: () => {
        const currentError = get().error;
        if (currentError) {
          set({
            error: {
              ...currentError,
              retryCount: currentError.retryCount + 1,
            },
          });
        }
      },

      reset: () => {
        set({
          ...initialState,
          rehydrated: get().rehydrated, // Preserve rehydration flag
        });
      },

      setRehydrated: (rehydrated: boolean) => {
        set({ rehydrated });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: STORAGE_VERSION,
      // Only persist essential data
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
        isAdmin: state.isAdmin,
        adminRole: state.adminRole,
        lastSuccessfulSync: state.lastSuccessfulSync,
        storageVersion: state.storageVersion,
        // Don't persist: sessionStatus, error, rehydrated
      }),
      // Migration handler for storage version changes
      migrate: (persistedState: any, version: number) => {
        if (version < STORAGE_VERSION) {
          // Clear old data, return fresh state
          return initialState;
        }
        return persistedState as AuthState;
      },
      // After rehydration callback
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setRehydrated(true);

          // If we have user/profile from storage, we're in hydrating state
          if (state.user && state.profile) {
            state.setSessionStatus("hydrating");
          } else {
            state.setSessionStatus("idle");
          }

          if (process.env.NODE_ENV === "development") {
            console.log("[AuthStore] Rehydration complete", {
              hasUser: !!state.user,
              hasProfile: !!state.profile,
              sessionStatus: state.sessionStatus,
            });
          }
        }
      },
    },
  ),
);

// Selectors for optimal re-render prevention
export const selectSessionStatus = (state: AuthState) => state.sessionStatus;
export const selectUser = (state: AuthState) => state.user;
export const selectProfile = (state: AuthState) => state.profile;
export const selectIsAdmin = (state: AuthState) => state.isAdmin;
export const selectAdminRole = (state: AuthState) => state.adminRole;
export const selectError = (state: AuthState) => state.error;
export const selectIsAuthenticated = (state: AuthState) =>
  state.sessionStatus === "authenticated";
export const selectIsLoading = (state: AuthState) =>
  state.sessionStatus === "initializing" ||
  state.sessionStatus === "hydrating" ||
  state.sessionStatus === "loading_profile" ||
  state.sessionStatus === "authenticating";
export const selectHasError = (state: AuthState) =>
  state.sessionStatus === "error";

// Helper to check if we should show loading UI
export const selectShouldShowLoading = (state: AuthState) => {
  return (
    state.sessionStatus === "initializing" ||
    state.sessionStatus === "hydrating" ||
    state.sessionStatus === "loading_profile"
  );
};

// Helper to check if session is stale
export const selectIsSessionStale = (state: AuthState) => {
  if (!state.lastSuccessfulSync) return true;
  const maxAge = 5 * 60 * 1000; // 5 minutes
  return Date.now() - state.lastSuccessfulSync > maxAge;
};
